import { SELECTED_ACCOUNT_CHAIN_ID } from '@hoodmorning/contracts';
import { keccak256, toHex, type Address, type Hex } from 'viem';
import { z } from 'zod';
import type { ExternalFixedRouteIdentityInput } from './externalFixedRouteIdentity.js';
import { hashPreparedCalls } from './calls.js';
import {
  prepareExternalOrderRegistration,
  prepareSelectedExternalOrderRegistration,
  prepareSelectedExternalPairedRegistration,
  prepareExternalPairedRegistration,
} from './externalOrderRegistration.js';
import {
  readExternalOrderObservationProfile,
  readSelectedExternalOrderObservationProfile,
  type ExternalOrderObservationProfile,
} from './externalOrderProfileObservation.js';

const decimalPattern = /^(0|[1-9][0-9]{0,77})$/;
const uint = (bits: number, positive = false) => z.string().regex(decimalPattern).refine(value =>
  BigInt(value) >= (positive ? 1n : 0n) && BigInt(value) < (1n << BigInt(bits)));
const address = z.string().regex(/^0x[0-9a-fA-F]{40}$/)
  .refine(value => !/^0x0{40}$/i.test(value))
  .transform(value => value.toLowerCase() as Address);
const hash = z.string().regex(/^0x[0-9a-fA-F]{64}$/)
  .refine(value => !/^0x0{64}$/i.test(value))
  .transform(value => value.toLowerCase() as Hex);
const displayedTrigger = z.strictObject({
  price: z.string().min(1).max(160).regex(/^(0|[1-9][0-9]*)(?:\.[0-9]+)?$/),
  atOrBelow: z.boolean(),
  latchAfterFirstFill: z.boolean(),
});
const leg = z.strictObject({
  minimumTotalNetOutput: uint(128, true),
  expiry: uint(48),
  trigger: displayedTrigger,
});
const terms = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('independent'),
    buy: z.boolean(),
    maximumInput: uint(128, true),
    minimumTotalNetOutput: uint(128, true),
    expiry: uint(48),
    trigger: displayedTrigger.nullable(),
  }),
  z.strictObject({
    kind: z.literal('linked'),
    maximumInput: uint(128, true),
    first: leg,
    second: leg,
  }),
]);
const envelope = z.strictObject({
  schemaVersion: z.literal(1),
  version: z.literal('external-fixed-route-order.1'),
  registrationId: z.uuid(),
  userId: z.string().min(1).max(128),
  chainId: z.literal(4663),
  engine: address,
  vault: address,
  owner: address,
  profile: z.unknown(),
  source: z.strictObject({
    blockNumber: uint(256),
    blockHash: hash,
    observedAt: z.iso.datetime(),
  }),
  terms,
});

type DisplayedTrigger = {
  price: string;
  atOrBelow: boolean;
  latchAfterFirstFill: boolean;
};

type ExternalOrderLeg = {
  minimumTotalNetOutput: string;
  expiry: string;
  trigger: DisplayedTrigger;
};

export type ExternalOrderIntent<ChainId extends 4663 | 46630 = 4663> = {
  schemaVersion: 1;
  version: 'external-fixed-route-order.1';
  registrationId: string;
  userId: string;
  chainId: ChainId;
  engine: Address;
  vault: Address;
  owner: Address;
  profile: ExternalOrderObservationProfile<ChainId>;
  source: { blockNumber: string; blockHash: Hex; observedAt: string };
  terms: {
    kind: 'independent';
    buy: boolean;
    maximumInput: string;
    minimumTotalNetOutput: string;
    expiry: string;
    trigger: DisplayedTrigger | null;
  } | {
    kind: 'linked';
    maximumInput: string;
    first: ExternalOrderLeg;
    second: ExternalOrderLeg;
  };
};

export type SelectedExternalOrderIntent = ExternalOrderIntent<typeof SELECTED_ACCOUNT_CHAIN_ID>;

function same(left: string | number, right: string | number) {
  return String(left).toLowerCase() === String(right).toLowerCase();
}

function freeze<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
    for (const nested of Object.values(value)) freeze(nested);
    Object.freeze(value);
  }
  return value;
}

function orderIntentCodec<const ChainId extends 4663 | 46630>(chainId: ChainId,
  readExternalOrderObservationProfile: (input: ExternalOrderObservationProfile<ChainId>) => ExternalOrderObservationProfile<ChainId>,
  prepareExternalOrderRegistration: (identity: ExternalFixedRouteIdentityInput<ChainId>,
    input: Parameters<typeof prepareSelectedExternalOrderRegistration>[1], feeBps?: number) => ReturnType<typeof prepareSelectedExternalOrderRegistration>,
  prepareExternalPairedRegistration: (identity: ExternalFixedRouteIdentityInput<ChainId>,
    input: Parameters<typeof prepareSelectedExternalPairedRegistration>[1], feeBps?: number) => ReturnType<typeof prepareSelectedExternalPairedRegistration>) {
  const domainEnvelope = envelope.extend({ chainId: z.literal(chainId) });
  /**
   * Restores one immutable, JSON-safe owner intent and reproduces the exact
   * unsigned fixed-route registration call. The embedded profile remains a
   * caller-trusted pin set: parsing it proves no runtime/source provenance,
   * canonicality, freshness, signature, submission or registration outcome.
   */
  function readExternalOrderIntent(input: unknown) {
    const parsed = domainEnvelope.parse(input);
    const profile = readExternalOrderObservationProfile(
      parsed.profile as ExternalOrderObservationProfile<ChainId>,
    );
    if (!same(parsed.chainId, profile.chainId)
      || !same(parsed.engine, profile.engine)
      || !same(parsed.vault, profile.vault)
      || !same(parsed.owner, profile.owner)) {
      throw new Error('External order intent profile context mismatch');
    }

    const record = freeze({ ...parsed, profile } as ExternalOrderIntent<ChainId>);
    const identity = {
      chainId: record.chainId,
      engine: record.engine,
      vault: record.vault,
      route: record.profile.route,
    };
    const expected = {
      globalEpoch: BigInt(record.profile.expectedGlobalEpoch),
      routeRevision: BigInt(record.profile.expectedRouteRevision),
    };
    const prepared = record.terms.kind === 'independent'
      ? prepareExternalOrderRegistration(identity, {
        expected,
        buy: record.terms.buy,
        maximumInput: BigInt(record.terms.maximumInput),
        minimumTotalNetOutput: BigInt(record.terms.minimumTotalNetOutput),
        expiry: BigInt(record.terms.expiry),
        trigger: record.terms.trigger,
      }, profile.routeIdVersion === 2 ? profile.feeBps : undefined)
      : prepareExternalPairedRegistration(identity, {
        expected,
        maximumInput: BigInt(record.terms.maximumInput),
        first: {
          minimumTotalNetOutput: BigInt(record.terms.first.minimumTotalNetOutput),
          expiry: BigInt(record.terms.first.expiry),
          trigger: record.terms.first.trigger,
        },
        second: {
          minimumTotalNetOutput: BigInt(record.terms.second.minimumTotalNetOutput),
          expiry: BigInt(record.terms.second.expiry),
          trigger: record.terms.second.trigger,
        },
      }, profile.routeIdVersion === 2 ? profile.feeBps : undefined);
    const recordJson = JSON.stringify(record);
    const call = freeze({
      to: prepared.call.target,
      value: 0n,
      data: prepared.call.data,
    });
    return freeze({
      record,
      recordJson,
      intentHash: keccak256(toHex(recordJson)),
      call,
      callsHash: hashPreparedCalls([call]),
      executionAuthorized: false as const,
      signingAuthorized: false as const,
      submissionAuthorized: false as const,
      registrationProven: false as const,
      profileApproved: false as const,
      sourceVerified: false as const,
      freshnessVerified: false as const,
      provenanceVerified: false as const,
      canonicalityVerified: false as const,
      verification: 'independent-owner-profile-source-freshness-submission-and-canonical-registration-required' as const,
    });
  }
  return readExternalOrderIntent;
}

export const readExternalOrderIntent = orderIntentCodec(4663, readExternalOrderObservationProfile,
  prepareExternalOrderRegistration, prepareExternalPairedRegistration);
export const readSelectedExternalOrderIntent = orderIntentCodec(SELECTED_ACCOUNT_CHAIN_ID,
  readSelectedExternalOrderObservationProfile, prepareSelectedExternalOrderRegistration, prepareSelectedExternalPairedRegistration);
export type SelectedExternalOrderIntentSource = ReturnType<typeof readSelectedExternalOrderIntent>;
